import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'

/**
 * Contrato de administración de la PLANTILLA del cartapacio (ADR 0022 §7.2).
 *
 * No es un catálogo más: no lleva listado con cursor, facetas ni búsqueda. Son
 * cinco a veinte filas que se leen enteras para pintar un árbol, y montarle el
 * estándar de listados sería vestir de paginación algo que nunca va a paginar.
 *
 * El `cat_` es la IDENTIDAD y `name` es la etiqueta. La distinción no es
 * cosmética: el id es la join key de la herencia, y por eso renombrar es libre
 * mientras que el id no se toca nunca.
 */

export const V2AdminDocumentCategorySchema = z.object({
  id: PublicIdSchemas.documentCategory,
  name: z.string(),
  parent: PublicIdSchemas.documentCategory.nullable(),
  order: z.number().int(),
  /**
   * Fecha de baja LÓGICA, o `null`. Una categoría deprecada deja de ofrecerse en
   * árboles nuevos pero sigue resolviendo donde ya hay contenido colgando: si
   * desapareciera, escondería archivos que alguien ya publicó.
   */
  deprecatedAt: InstantSchema.nullable(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

export const V2AdminDocumentCategoriesSchema = z.object({
  data: z.array(V2AdminDocumentCategorySchema)
})

export const V2CreateAdminDocumentCategorySchema = z.object({
  name: requiredString('El nombre de la categoría es obligatorio.'),
  parent: PublicIdSchemas.documentCategory.nullish(),
  order: z.number().int().optional()
})

export const V2UpdateAdminDocumentCategorySchema = z.object({
  name: requiredString('El nombre de la categoría es obligatorio.').optional(),
  order: z.number().int().optional()
})

/**
 * Ajuste LOCAL de una categoría heredada, con alcance de un solo dueño.
 *
 * `name` y `order` en `null` significan «volvé a lo que dice la plantilla», que es
 * distinto de omitirlos: omitir deja el ajuste como está.
 */
export const V2UpsertDocumentCategoryOverrideSchema = z.object({
  name: z.string().nullish(),
  order: z.number().int().nullish(),
  /**
   * Oculta la categoría para este dueño. Queda en bitácora y la vista interna la
   * sigue mostrando marcada — ocultar en silencio es cómo un documento que se
   * debía no llega.
   */
  hidden: z.boolean().optional()
})

export type V2AdminDocumentCategory = z.infer<typeof V2AdminDocumentCategorySchema>
export type V2CreateAdminDocumentCategory = z.infer<typeof V2CreateAdminDocumentCategorySchema>
export type V2UpdateAdminDocumentCategory = z.infer<typeof V2UpdateAdminDocumentCategorySchema>
export type V2UpsertDocumentCategoryOverride = z.infer<
  typeof V2UpsertDocumentCategoryOverrideSchema
>
